import { baseUrl } from "./index";
import { stripStoredUserTokenSuffix } from "./token";

// Shared "Apply for Finance" submission. Both the New Build detail page and the
// off-market property page used to inline near-identical copies of this logic; this
// module is the single source (CLAUDE.md §2d/§2j).

const FINANCE_APPLY_PATH = "/whitelabel/financeapply";
const DEFAULT_REFERRER_NAME = "Whitelabel Website";
export const FINANCE_SUCCESS_MESSAGE =
  "Your finance application has been submitted successfully.";

// Backend (tva-api) validates these in order and 400s "Missing required field: <Label>".
const MISSING_FIELD_PREFIX = "Missing required field: ";

// The user-supplied fields the finance endpoint requires. Property Address is derived
// from the property, not the user, so it is not part of this set.
export type FinanceUserField = "firstName" | "lastName" | "email" | "phone";

// All user-editable profile fields, in display order. Used to let the user correct any
// of them when a save/re-submit fails on a field that wasn't initially blank (e.g. a
// malformed but non-empty email).
export const FINANCE_USER_FIELDS: FinanceUserField[] = [
  "firstName",
  "lastName",
  "email",
  "phone",
];

// Maps each user field to the human label the backend uses in its error message.
const USER_FIELD_LABELS: Record<FinanceUserField, string> = {
  firstName: "First Name",
  lastName: "Last Name",
  email: "Email",
  phone: "Phone",
};

// Shape returned by GET /user/profile (snake_case).
export interface FinanceProfileInput {
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
}

// Request body the finance endpoint expects.
export interface FinanceFormData {
  "First Name": string;
  "Last Name": string;
  Phone: string;
  Email: string;
  "Property Address": string;
  "Website Referred From": string;
}

export interface FinanceApplyContext {
  token: string; // raw stored token (suffix is stripped here)
  profile: FinanceProfileInput;
  propertyAddress: string;
  referredFrom?: string;
}

export type FinanceApplyResult =
  | { kind: "success"; message: string }
  | { kind: "incompleteProfile"; missingFields: FinanceUserField[] }
  | { kind: "error"; message: string };

const isBlank = (value?: string): boolean => !value || value.trim() === "";

// Which required user fields are missing/blank on the profile.
export function missingFinanceProfileFields(
  profile: FinanceProfileInput
): FinanceUserField[] {
  const missing: FinanceUserField[] = [];
  if (isBlank(profile.first_name)) missing.push("firstName");
  if (isBlank(profile.last_name)) missing.push("lastName");
  if (isBlank(profile.email)) missing.push("email");
  if (isBlank(profile.phone)) missing.push("phone");
  return missing;
}

// Maps a backend "Missing required field: X" message back to a user field, if X is one.
function userFieldFromServerMessage(
  message: string
): FinanceUserField | null {
  if (!message.startsWith(MISSING_FIELD_PREFIX)) return null;
  const label = message.slice(MISSING_FIELD_PREFIX.length).trim();
  const entry = (Object.keys(USER_FIELD_LABELS) as FinanceUserField[]).find(
    (field) => USER_FIELD_LABELS[field] === label
  );
  return entry ?? null;
}

export async function submitFinanceApplication(
  ctx: FinanceApplyContext
): Promise<FinanceApplyResult> {
  // Pre-check: don't fire a request we know will be rejected.
  const missing = missingFinanceProfileFields(ctx.profile);
  if (missing.length > 0) {
    return { kind: "incompleteProfile", missingFields: missing };
  }

  const cleanToken = stripStoredUserTokenSuffix(ctx.token);
  const formData: FinanceFormData = {
    "First Name": ctx.profile.first_name || "",
    "Last Name": ctx.profile.last_name || "",
    Phone: ctx.profile.phone || "",
    Email: ctx.profile.email || "",
    "Property Address": ctx.propertyAddress,
    "Website Referred From": ctx.referredFrom || DEFAULT_REFERRER_NAME,
  };

  const response = await fetch(`${baseUrl}${FINANCE_APPLY_PATH}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "x-auth-token": cleanToken,
      Authorization: `Bearer ${cleanToken}`,
    },
    body: JSON.stringify(formData),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const message: string =
      (errorData && errorData.message) || "Failed to submit finance application";
    const field = userFieldFromServerMessage(message);
    if (field) {
      return { kind: "incompleteProfile", missingFields: [field] };
    }
    return { kind: "error", message };
  }

  return { kind: "success", message: FINANCE_SUCCESS_MESSAGE };
}
